import { FC, useEffect, useState } from "react";
import { getContactData } from "../../types/components";
import contactdata from "../../services/contactdata.service";
import Toast from "../../components/ui/Toast";
import Pagination from "../../components/ui/pagination";

const AgentInquires: FC = () => {
  const [ContactData, setContactData] = useState<getContactData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedContact, setSelectedContact] = useState<getContactData | null>(
    null
  );
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  useEffect(() => {
    loadData();
  }, [currentPage, itemsPerPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await contactdata.getContactData(
        currentPage,
        itemsPerPage
      );

      const extractPaginationData = (response: any) => {
        if (response?.success && response?.data) {
          return {
            data: response.data,
            totalCount: response.totalCount,
            totalPages: response.totalPages,
            currentPage: response.currentPage || currentPage,
            pageSize: response.pageSize || itemsPerPage,
          };
        } else if (response?.status === "success" && response?.data) {
          const data = response.data;
          return {
            data: data.data || data.items || data || [],
            totalCount: data.totalCount || data.total || 0,
            totalPages:
              data.totalPages ||
              Math.ceil((data.totalCount || data.total || 0) / itemsPerPage),
            currentPage: data.currentPage || currentPage,
            pageSize: data.pageSize || itemsPerPage,
          };
        } else if (response?.data) {
          const data = response.data;
          return {
            data: data.data || data.items || data || [],
            totalCount: data.totalCount || data.total || 0,
            totalPages:
              data.totalPages ||
              Math.ceil((data.totalCount || data.total || 0) / itemsPerPage),
            currentPage: data.currentPage || currentPage,
            pageSize: data.pageSize || itemsPerPage,
          };
        } else if (Array.isArray(response)) {
          return {
            data: response,
            totalCount: response.length,
            totalPages: Math.ceil(response.length / itemsPerPage),
            currentPage: currentPage,
            pageSize: itemsPerPage,
          };
        }

        return {
          data: [],
          totalCount: 0,
          totalPages: 0,
          currentPage: currentPage,
          pageSize: itemsPerPage,
        };
      };

      const paginatedData = extractPaginationData(response);

      const sortedData = paginatedData.data.contactDetails.sort(
        (a: getContactData, b: getContactData) => {
          const dateA = new Date(a.resolvedDate || a.resolvedDate || 0);
          const dateB = new Date(b.resolvedDate || b.resolvedDate || 0);
          return dateB.getTime() - dateA.getTime();
        }
      );

      if (response) {
        setContactData(sortedData);
        setTotalCount(paginatedData.totalCount);
        setTotalPages(paginatedData.totalPages);
      } else {
        console.error("Failed to load contact data");
        setToast({
          message: "Failed to load contact data",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error loading contact data:", error);
      setToast({
        message: "Error loading contact data",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickResolve = async (
    contact: getContactData,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      Object.keys(contact).forEach((key) => {
        console.log(`  ${key}:`, contact[key as keyof getContactData]);
      });

      // Try to find ANY ID property
      const possibleIdKeys = [
        "ContactId",
        "contactId",
        "id",
        "Id",
        "contactID",
        "contact_id",
        "contactID",
        "contactUuid",
      ];
      let contactId: string | number | undefined;

      for (const key of possibleIdKeys) {
        if (contact[key as keyof getContactData]) {
          contactId = contact[key as keyof getContactData] as any;
          break;
        }
      }

      if (!contactId) {
        console.error(
          "No ID property found in contact object. Available keys:",
          Object.keys(contact)
        );
        setToast({
          message: "Cannot update: No valid contact ID found",
          type: "error",
        });
        return;
      }
      const checked = e.target.checked;
      const updatedData = {
        ContactId: contactId.toString(),
        fullName: contact.fullName,
        email: contact.email,
        subject: contact.subject,
        mobileNumber: contact.mobileNumber,
        message: contact.message,
        isResolve: checked,
        resolvedDate: checked
          ? new Date().toISOString()
          : new Date("0001-01-01T00:00:00Z"),
        resolvedBy: contact.ContactId,
        recaptchaToken: "",
      };

      await contactdata.updatedData(updatedData);

      setContactData((prev) =>
        prev.map((item) =>
          item.ContactId === contact.ContactId
            ? {
                ...item,
                isresolve: checked,
                resolvedDate: updatedData.resolvedDate,
              }
            : item
        )
      );

      setToast({
        message: `Contact ${"data updated successfully.."}!`,
        type: "success",
      });
      loadData();
    } catch (error: any) {
      console.error("Error updating resolve status:", error);
      setToast({
        message: "Failed to update contact status",
        type: "error",
      });
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Inquiry ({totalCount})
          </h2>
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  mobile number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  is resolve
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action taken
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ContactData && ContactData.length > 0 ? (
                ContactData.map((contact) => (
                  <tr
                    key={contact.ContactId}
                    className="hover:bg-primary-teaGreen/30"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {contact.fullName || "No name"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.subject}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.mobileNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.isResolve ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Resolved
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`quick-resolve-${contact.ContactId}`}
                          checked={
                            contact.isResolve || contact.isResolve || false
                          }
                          onChange={(e) => handleQuickResolve(contact, e)}
                          className="w-4 h-4 items-center"
                          style={{
                            accentColor: "#16a34a",
                            margin: "0 35px",
                            display: "block",
                            cursor: "pointer",
                            borderRadius: "4px",
                            border: "2px solid #d1d5db",
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    {loading ? "Loading inquiries..." : "No inquiries found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalCount}
          onPageChange={handlePageChange}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          getPageNumbers={getPageNumbers}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={3000}
          onClose={() => setToast(null)}
          show
        />
      )}
    </div>
  );
};

export default AgentInquires;
